import {
  faAdd,
  faChainSlash,
  faChevronRight,
  faCode,
  faCog,
  faExternalLinkAlt,
  faGlobe,
  faKey,
  faPencil,
  faServer,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useApiClient } from "@hostingbv/lib/src/api";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { Card } from "@hostingbv/lib/src/components/Card";
import { Copyable } from "@hostingbv/lib/src/components/content/Copyable";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { useModal } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { FC, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { Site } from "../../../lib/src/api/partner/models/Site";
import { config } from "../config";
import { AddDeploymentKeyModal } from "../modals/AddDeploymentKeyModal";
import { AddSiteAliasDomainModal } from "../modals/AddSiteAliasDomainModal";
import { EditPhpVersionModal } from "../modals/EditPhpVersionModal";
import { MoveToCustomerModal } from "../modals/MoveToCustomerModal";
import { PlannedRemovalBanner } from "./PlannedRemovalBanner";
import {
  useDomains,
  useSshKeyAdd,
  useSshKeyDel,
  useUpdateWindhostingSite,
  useWindhostingSite,
  useWindhostingSitePHPInfo,
  useWindhostingSiteWebserverInfo,
} from "@hostingbv/lib/src/api/partner/PartnerHooks";
import { DeploymentHistoryBlock } from "./deployments/DeploymentHistoryBlock";
import { WindhostingSiteWebserverConfigModal } from "../modals/WindhostingSiteWebserverConfig";
import { SiteConfig } from "@hostingbv/lib/src/api/partner/models/windhosting/SiteConfig";
import { WindhostingSitePHPPoolConfigModal } from "../modals/WindhostingSitePHPPoolConfigModal";
import { DeleteSiteModal } from "../modals/DeleteSiteModal";
import { WindhostingSiteTLSConfigModal } from "../modals/WindhostingSiteTLSConfigModal";
import { useRealtimeDeployments } from "./deployments/RealtimeDeployments";
import { useRefresh } from "@hostingbv/lib/src/components/RefreshController";

export const WindhostingSiteDetails: FC<{ site: Site }> = ({ site }) => {
  return (
    <>
      <PlannedRemovalBanner site={site} />
      <DetailsBlock site={site} />
      <WebserverBlock site={site} />
      <PhpBlock site={site} />
      <AliasDomainsBlock site={site} />
      <SshBlock site={site} />
      <DeploymentHistoryBlock entityUUID={site.id} />
    </>
  );
};

const DetailsBlock: FC<{ site: Site }> = ({ site }) => {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const { open: openDelete } = useModal({
    title: "Delete site",
    size: "md",
    body: <DeleteSiteModal entity={site} />,
  });

  const { data: domains } = useDomains();
  const { data: webserver } = useWindhostingSiteWebserverInfo(site.serverId, site.id);
  const domain = useMemo(() => {
    return (
      (domains ?? []).find(
        (domain) =>
          domain.displayName === site.displayName ||
          site.displayName.endsWith(`.${domain.displayName}`),
      ) ?? null
    );
  }, [domains, site]);

  const { open: openCustomerModal } = useModal({
    title: "Move to customer",
    body: (
      <MoveToCustomerModal
        currentCustomer={site.customer!}
        save={(c) => partnerClient.setSiteCustomer(site.id, c.id)}
      />
    ),
  });
  return (
    <>
      <Card title={"Site details"}>
        <div className={"flex items-stretch"}>
          <div className="flex-1">
            <dl>
              <dt className={"text-slate-500 text-xs"}>Address</dt>
              <dd className={"text-slate-900 mb-4"}>
                <a
                  href={`https://${site.displayName}`}
                  target={"_blank"}
                  className={"text-brand-500 underline"}
                >
                  {site.displayName}{" "}
                  <FontAwesomeIcon
                    icon={faExternalLinkAlt}
                    className={"text-xs inline align-baseline"}
                  />
                </a>
              </dd>
              <dt className={"text-slate-500 text-xs"}>Hostname</dt>
              <dd className={"text-slate-900 mb-4"}>
                <div className={"flex flex-wrap space-x-2 mt-1"}>
                  <Copyable text={site.server?.hostname ?? "-"} />
                </div>
              </dd>
              {site.server && (
                <>
                  <dt className={"text-slate-500 text-xs"}>IP Address</dt>
                  <dd className={"text-slate-900 mb-4"}>
                    <div className={"flex flex-wrap space-x-2 mt-1"}>
                      {site.server.ipAddresses.map((ip, i) => (
                        <Copyable key={i} text={ip} />
                      ))}
                    </div>
                  </dd>
                  <dt className={"text-slate-500 text-xs"}>Webserver</dt>
                  <dd className={"text-slate-900 mb-4"}>
                    {webserver?.component ?? "-"}
                  </dd>
                </>
              )}
              <dt className={"text-slate-500 text-xs"}>SSH Login</dt>
              <dd className={"text-slate-900 mb-4"}>
                <Copyable
                  text={
                    site.name +
                    "@" +
                    (site.server?.ipAddresses.find(Boolean) ?? "-")
                  }
                />
              </dd>
              {site.customer && (
                <>
                  <dt className={"text-slate-500 text-xs"}>Customer</dt>
                  <dd className={"text-slate-900 mb-4"}>
                    <div className={"flex items-center space-x-3"}>
                      <span>{site.customer?.displayName}</span>
                      {can("edit", "sites", site.permissions) && (
                        <button
                          className={
                            "h-6 w-6 border border-slate-200 rounded-sm text-slate-500 hover:bg-slate-50 text-xs"
                          }
                          onClick={openCustomerModal}
                        >
                          <FontAwesomeIcon icon={faPencil} />
                        </button>
                      )}
                    </div>
                  </dd>
                </>
              )}
            </dl>
          </div>

          {can("delete", "sites", site.permissions) && (
            <div className={"flex flex-col items-end space-y-4"}>
              <Button
                type={"danger"}
                size={"sm"}
                text={"Delete"}
                icon={faTrash}
                onClick={() => openDelete()}
              />
            </div>
          )}
        </div>
        {(site.server || domain) && (
          <hr className={"-mx-6 border-slate-200"} />
        )}
        <div className={"flex flex-col xl:space-x-4 xl:flex-row"}>
          {site.server && (
            <NavLink
              className={
                "mt-4 py-3 pr-4 border border-slate-100 rounded-sm w-full flex items-center justify-between hover:bg-slate-50"
              }
              to={`/servers/${site.serverId}`}
            >
              <div
                className={
                  "w-8 h-8 mx-4 rounded-full bg-brand-100 flex items-center justify-center"
                }
              >
                <FontAwesomeIcon icon={faServer} className={"text-brand-500"} />
              </div>
              <dl className={"flex flex-col items-start flex-1"}>
                <dt className={"text-slate-500 text-xs"}>Server</dt>
                <dd className={"text-slate-900"}>{site.server.displayName}</dd>
              </dl>
              <div
                className={
                  "text-brand-600 flex items-center justify-end space-x-3"
                }
              >
                <div className={"text-xs uppercase tracking-wide font-medium"}>
                  Details
                </div>{" "}
                <FontAwesomeIcon icon={faChevronRight} />
              </div>
            </NavLink>
          )}
          {domain && (
            <NavLink
              className={
                "mt-4 py-3 pr-4 border border-slate-100 rounded-sm w-full flex items-center justify-between hover:bg-slate-50"
              }
              to={`/domains/${domain?.id}`}
            >
              <div
                className={
                  "w-8 h-8 mx-4 rounded-full bg-brand-100 flex items-center justify-center"
                }
              >
                <FontAwesomeIcon icon={faGlobe} className={"text-brand-500"} />
              </div>
              <dl className={"flex flex-col items-start flex-1"}>
                <dt className={"text-slate-500 text-xs"}>Domain & DNS</dt>
                <dd className={"text-slate-900"}>{domain.displayName}</dd>
              </dl>
              <div
                className={
                  "text-brand-600 flex items-center justify-end space-x-3"
                }
              >
                <div className={"text-xs uppercase tracking-wide font-medium"}>
                  Details
                </div>{" "}
                <FontAwesomeIcon icon={faChevronRight} />
              </div>
            </NavLink>
          )}
        </div>
      </Card>
    </>
  );
};

const WebserverBlock: FC<{ site: Site }> = ({ site }) => {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);

  const { data: webserver } = useWindhostingSiteWebserverInfo(site.serverId, site.id);

  const { open: openWebserverConfig } = useModal({
    title: "Webserver config",
    body: <WindhostingSiteWebserverConfigModal site={site} />,
  });

  const { open: openTLSConfig } = useModal({
    title: "Webserver config",
    body: <WindhostingSiteTLSConfigModal site={site} />,
  });

  // Both rows only open editors, so the card would be empty without edit access.
  if (!can("edit", "sites", site.permissions)) {
    return null;
  }

  return (
    <Card title={"Webserver"} label={webserver?.component ?? ""}>
      <div className={"flex items-center"}>
        <div
          className={
            "h-12 w-12 rounded-full bg-brand-50 flex items-center justify-center"
          }
        >
          <FontAwesomeIcon icon={faCode} className={"text-brand-500"} />
        </div>
        <div className={"flex-1 mx-6"}>
          <h2 className={"font-medium text-lg"}>Webserver Config</h2>
          <p className={"text-slate-800 text-sm max-w-lg"}>
            Make custom changes to the webserver config, beware for breaking
            changes.
          </p>
        </div>
        <Button
          type={"secondary"}
          icon={faCog}
          size={"sm"}
          text={"Config"}
          onClick={() => openWebserverConfig()}
        />
      </div>

      <hr className="-mx-6 my-4 border-slate-200" />
      <div className={"flex items-center"}>
        <div
          className={
            "h-12 w-12 rounded-full bg-brand-50 flex items-center justify-center"
          }
        >
          <FontAwesomeIcon icon={faCode} className={"text-brand-500"} />
        </div>
        <div className={"flex-1 mx-6"}>
          <h2 className={"font-medium text-lg"}>TLS Certificate</h2>
          <p className={"text-slate-800 text-sm max-w-lg"}>
            Make custom changes to the webserver config, beware for breaking
            changes.
          </p>
        </div>
        <Button
          type={"secondary"}
          icon={faCog}
          size={"sm"}
          text={"Config"}
          onClick={() => openTLSConfig()}
        />
      </div>
    </Card>
  );
};

const PhpBlock: FC<{ site: Site }> = ({ site }) => {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);

  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!;
  const refresh = useRefresh();
  const { data: info } = useWindhostingSitePHPInfo(site.serverId, site.id);
  const { data: conf } = useWindhostingSite(site.serverId, site.id);
  const { mutate: updateSite } = useUpdateWindhostingSite(
    site.serverId,
    site.id,
  );
  const { open: openPHPVersionModal } = useModal({
    title: "Change PHP version",
    body: (
      <EditPhpVersionModal
        active={conf?.phpVersion ?? null}
        availableVersions={
          info?.phpVersions.reduce((acc, x) => ({ ...acc, [x]: x }), {}) ?? []
        }
        save={async (phpVersion) => {
          if (!conf) return;
          const deployment = await updateSite(
            new SiteConfig(
              conf.certificate,
              phpVersion,
              conf.phpPoolConfig,
              conf.webserverConfig,
              conf.aliasDomains,
            ),
          );
          await awaitNextDeploymentFinished(deployment.id);
          refresh();
        }}
      />
    ),
  });

  const { open: openPoolConfigModal } = useModal({
    title: "Webserver config",
    body: <WindhostingSitePHPPoolConfigModal site={site} />,
  });

  return (
    <Card title={"PHP"}>
      <ul className={"flex flex-wrap gap-4"}>
        <dl>
          <dt className={"text-slate-500 text-xs"}>PHP Version</dt>
          <dd className={"text-slate-900"}>
            <div className={"flex items-center space-x-3"}>
              <span>{conf?.phpVersion ? `PHP ${conf.phpVersion}` : "-"}</span>
              {can("edit", "sites", site.permissions) && (
                <button
                  className={
                    "h-6 w-6 border border-slate-200 rounded-sm text-slate-500 hover:bg-slate-50 text-xs"
                  }
                  onClick={openPHPVersionModal}
                >
                  <FontAwesomeIcon icon={faPencil} />
                </button>
              )}
            </div>
          </dd>
        </dl>
      </ul>

      {can("edit", "sites", site.permissions) && (
        <>
          <hr className="-mx-6 my-4 border-slate-200" />
          <div className={"flex items-center"}>
            <div
              className={
                "h-12 w-12 rounded-full bg-brand-50 flex items-center justify-center"
              }
            >
              <FontAwesomeIcon icon={faCode} className={"text-brand-500"} />
            </div>
            <div className={"flex-1 mx-6"}>
              <h2 className={"font-medium text-lg"}>PHP-FPM Pool Config</h2>
              <p className={"text-slate-800 text-sm max-w-lg"}>
                Make custom changes to the php-fpm config, beware for breaking
                changes.
              </p>
            </div>
            <Button
              type={"secondary"}
              icon={faCog}
              size={"sm"}
              text={"Config"}
              onClick={() => openPoolConfigModal()}
            />
          </div>
        </>
      )}
    </Card>
  );
};

const AliasDomainsBlock: FC<{ site: Site }> = ({ site }) => {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!;
  const refresh = useRefresh();

  const { data: conf } = useWindhostingSite(site.serverId, site.id);
  const { mutate: updateSite } = useUpdateWindhostingSite(
    site.serverId,
    site.id,
  );

  const { open } = useModal({
    title: "Add alias domains",
    body: <AddSiteAliasDomainModal site={site} />,
  });

  const removeAlias = async (aliasDomain: string) => {
    if (!conf) return;
    const deployment = await updateSite(
      new SiteConfig(
        conf.certificate,
        conf.phpVersion,
        conf.phpPoolConfig,
        conf.webserverConfig,
        conf.aliasDomains.filter((x) => x !== aliasDomain),
      ),
    );
    await awaitNextDeploymentFinished(deployment.id);
    refresh();
  };

  const aliasDomains = conf?.aliasDomains ?? [];
  return (
    <Card title={"Alias domains"}>
      <Paragraph>
        Additional domains that serve this site alongside {site.displayName}.
        Make sure the site's TLS certificate also covers any alias domains added
        here.
      </Paragraph>
      {aliasDomains.length === 0 && (
        <Paragraph>No alias domains have been added to this site</Paragraph>
      )}
      <ul className={"flex flex-wrap gap-4 mb-6"}>
        {aliasDomains.map((aliasDomain, k) => (
          <li key={k}>
            <div
              className={
                "flex justify-start items-center h-12 min-w-62.5 text-sm rounded-lg font-medium px-4 py-3 bg-slate-100 text-slate-900"
              }
            >
              <FontAwesomeIcon icon={faGlobe} className={"mr-2"} />
              <span className={"flex-1 text-left mx-2"}>{aliasDomain}</span>
              {can("edit", "sites", site.permissions) && (
                <button
                  className={
                    "h-6 w-6 border border-slate-300 rounded-sm text-slate-600 hover:bg-slate-200 hover:text-red-600 text-xs"
                  }
                  onClick={() => removeAlias(aliasDomain)}
                >
                  <FontAwesomeIcon icon={faChainSlash} />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {can("edit", "sites", site.permissions) && (
        <Button
          type={"secondary"}
          size={"sm"}
          text={"Add alias domains"}
          icon={faAdd}
          onClick={open}
        />
      )}
    </Card>
  );
};

const SshBlock: FC<{ site: Site }> = ({ site }) => {
  const { mutate: addKey } = useSshKeyAdd(site.id);
  const { mutate: delKey } = useSshKeyDel(site.id);
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!;
  const refresh = useRefresh();

  const { open } = useModal({
    title: "Add SSH key",
    body: (
      <AddDeploymentKeyModal
        filterKeys={(k) => !site.sshKeys?.find((x) => x.id === k.id)}
        onKeySelected={async (key) => {
          const deployment = await addKey(key.id);
          if (deployment) await awaitNextDeploymentFinished(deployment.id);
          refresh();
        }}
      />
    ),
  });

  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const removeKey = async (keyId: string) => {
    const deployment = await delKey(keyId);
    if (deployment) await awaitNextDeploymentFinished(deployment.id);
    refresh();
  };
  return (
    <Card title={"SSH access"}>
      {site.sshKeys?.length === 0 && (
        <Paragraph>No keys have access to this site</Paragraph>
      )}
      <ul className={"flex flex-wrap gap-4 mb-6"}>
        {site.sshKeys?.map((key) => {
          return (
            <li key={key.id}>
              <div
                className={`flex justify-start items-center h-12 min-w-62.5 text-sm rounded-lg font-medium px-4 py-3 bg-slate-100 text-slate-900`}
              >
                <FontAwesomeIcon icon={faKey} className={"mr-2"} />
                <span className={"flex-1 text-left mx-2"}>
                  {key.displayName}
                </span>
                {can("edit", "sites", site.permissions) && (
                  <button
                    className={
                      "h-6 w-6 border border-slate-300 rounded-sm text-slate-600 hover:bg-slate-200 hover:text-red-600 text-xs"
                    }
                    onClick={() => removeKey(key.id)}
                  >
                    <FontAwesomeIcon icon={faChainSlash} />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {can("edit", "sites", site.permissions) && (
        <Button
          type={"secondary"}
          size={"sm"}
          text={"Add key"}
          icon={faAdd}
          onClick={open}
        />
      )}
    </Card>
  );
};
